// apps/.../wet/components/StickyCTA.tsx
'use client';
import React, { useEffect, useRef, useState } from 'react';

/* GA helper: local only */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return; // parent-only iframes: keep this
    const w: any = window;
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...(params || {}) });
      return;
    }
    // Fallback if gtag is still present in older deploys
    if (typeof w.gtag === 'function') {
      w.gtag('event', event, params || {});
    }
  } catch {}
}


/* Optional WP override */
const WP_MARKETING_ENDPOINT = 'https://wateredgetech.com/wp-json/wet/v1/marketing';

// Accept an optional 'className' prop
const StickyCTA: React.FC<{ className?: string }> = ({ className }) => {
  const [label, setLabel] = useState('Schedule a Free Water Test');
  const [href, setHref] = useState('#contact');
  const [ctaId, setCtaId] = useState('sticky_water_test');

  // Pull overrides if present
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        const sec = data?.sections || {};
        if (cancelled) return;
        if (typeof sec.cta_label === 'string' && sec.cta_label.trim()) setLabel(sec.cta_label.trim());
        if (typeof sec.cta_href === 'string' && sec.cta_href.trim()) setHref(sec.cta_href.trim());
        if (typeof sec.cta_id === 'string' && sec.cta_id.trim()) setCtaId(sec.cta_id.trim());
      } catch {}
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  // Impression once when visible
  const ref = useRef<HTMLAnchorElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (fired) return;
        if (entries.some((e) => e.isIntersecting)) {
          fired = true;
          ga('cta_impression', { cta_id: ctaId, href });
          io.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [href, ctaId]);

  const handleClick = () => {
    ga('cta_click', { cta_id: ctaId, href, location: 'sticky_fab' });
  };

  return (
    <a
      ref={ref}
      href={href}
      onClick={handleClick}
      // MODIFIED: Removed 'animate-pulse' and combined with className prop
      className={`bg-teal-500 text-white font-bold px-6 py-3 rounded-full hover:bg-teal-600 transition duration-300 shadow-lg text-sm ${
        className || 'fixed bottom-5 right-5 z-40'
      }`}
    >
      {label}
    </a>
  );
};

export default StickyCTA;